import React, { useEffect, useState, useMemo, useCallback } from 'react';
import api from '../../../utils/api';
import { toLocalDateString } from '../../../utils/dateUtils';
import Pagination, { PAGE_SIZE } from '../../../components/portal/Pagination';
import {
  RiFileList3Line, RiSearchLine, RiTimeLine, RiRefreshLine,
  RiUserLine, RiArrowDownSLine, RiArrowUpSLine,
  RiDeleteBinLine, RiCalendarLine, RiFilterLine, RiCloseLine,
} from 'react-icons/ri';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

function formatDateTime(raw) {
  if (!raw) return '—';
  return new Date(raw).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function getInitials(name, email) {
  return (name || email || 'U').split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);
}

// ── Display helper: shows only sections that have content (renders rich HTML) ─
function UpdateSections({ u }) {
  const completed   = u.completed   || '';
  const inProgress  = u.in_progress || u.inProgress || '';
  const notes       = u.notes       || '';
  const legacy      = u.content     || u.text       || '';
  const isHTMLEmpty = (s) => !s || String(s).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length === 0;
  const hasNew      = !isHTMLEmpty(completed) || !isHTMLEmpty(inProgress) || !isHTMLEmpty(notes);

  if (!hasNew) {
    return (
      <p className="text-[#1F2937] text-sm leading-relaxed whitespace-pre-wrap">
        {legacy || '—'}
      </p>
    );
  }

  const Section = ({ color, label, value }) => {
    if (isHTMLEmpty(value)) return null;
    return (
      <div className="flex gap-2.5">
        <div className="w-1 rounded-full flex-shrink-0" style={{ background: color }} />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wide mb-0.5" style={{ color }}>{label}</p>
          <div
            className="rich-content text-[#1F2937] text-sm leading-relaxed"
            dangerouslySetInnerHTML={{ __html: value }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-2.5">
      <Section color="#10b981" label="Completed"   value={completed} />
      <Section color="#0984e3" label="In Progress" value={inProgress} />
      <Section color="#7c3aed" label="Notes"       value={notes} />
    </div>
  );
}

// ── Employee accordion card ────────────────────────────────────────────────────
function EmployeeSection({ name, email, updates, defaultOpen = false, onDelete }) {
  const [open,       setOpen]       = useState(defaultOpen);
  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (id) => {
    setDeletingId(id);
    try { await onDelete(id); } finally { setDeletingId(null); }
  };

  // group this employee's updates by date label
  const byDate = useMemo(() => {
    return updates.reduce((acc, u) => {
      const raw   = u.date || u.createdAt;
      const label = raw
        ? new Date(raw).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
        : 'Unknown date';
      if (!acc[label]) acc[label] = [];
      acc[label].push(u);
      return acc;
    }, {});
  }, [updates]);

  const todayStr = toLocalDateString(new Date());

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] overflow-hidden">

      {/* header */}
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-[#FAFBFC] transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-4">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
            style={{ background: 'linear-gradient(135deg,#0984e3 0%,#d500f9 100%)' }}
          >
            {getInitials(name, email)}
          </div>
          <div className="text-left">
            <p className="text-[#1F2937] font-semibold text-sm">{name || '—'}</p>
            <p className="text-[#9CA3AF] text-xs">{email}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="badge">{updates.length} update{updates.length !== 1 ? 's' : ''}</span>
          {open
            ? <RiArrowUpSLine   size={18} className="text-[#9CA3AF]" />
            : <RiArrowDownSLine size={18} className="text-[#9CA3AF]" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-[#F3F4F6]">
          {updates.length === 0 ? (
            <p className="px-6 py-6 text-[#9CA3AF] text-sm text-center">No updates found.</p>
          ) : (
            Object.entries(byDate).map(([dateLabel, items]) => {
              const dateKey  = (items[0].date || items[0].createdAt || '').slice(0, 10);
              const isToday  = dateKey === todayStr;
              return (
                <div key={dateLabel}>
                  {/* date group label */}
                  <div className="sticky top-0 z-10 px-6 py-2 bg-[#F8FAFC] border-b border-[#F3F4F6] flex items-center gap-2">
                    <RiCalendarLine size={12} className="text-[#9CA3AF]" />
                    <span className="text-xs font-semibold text-[#6B7280]">{dateLabel}</span>
                    {isToday && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-[#10b981] bg-[rgba(16,185,129,0.1)]">Today</span>
                    )}
                  </div>

                  <div className="divide-y divide-[#F3F4F6]">
                    {items.map((u) => {
                      const id = u._id || u.id;
                      return (
                        <div key={id} className="px-6 py-4 hover:bg-[#FAFBFC] transition-colors">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="text-[#9CA3AF] text-xs flex items-center gap-1 mb-2">
                                <RiTimeLine size={11} />
                                {formatDateTime(u.date || u.createdAt)}
                              </p>
                              <UpdateSections u={u} />
                            </div>
                            <button
                              onClick={() => handleDelete(id)}
                              disabled={deletingId === id}
                              className="p-1.5 text-[#9CA3AF] hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer flex-shrink-0 disabled:opacity-50 mt-0.5"
                              aria-label="Delete update"
                            >
                              {deletingId === id
                                ? <span className="w-3.5 h-3.5 border border-red-400 border-t-transparent rounded-full animate-spin block" />
                                : <RiDeleteBinLine size={14} />}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────
export default function AdminDailyUpdates() {
  const today = new Date();

  // ── server-side filters (month / year / user_id) ──────────────────────────
  const [filterMonth,  setFilterMonth]  = useState(today.getMonth() + 1);
  const [filterYear,   setFilterYear]   = useState(today.getFullYear());
  const [filterUserId, setFilterUserId] = useState('');   // employee UUID

  // ── client-side filters (date + search) ──────────────────────────────────
  const [dateFilter, setDateFilter] = useState('');
  const [search,     setSearch]     = useState('');

  // ── data ──────────────────────────────────────────────────────────────────
  const [updates,   setUpdates]   = useState([]);
  const [employees, setEmployees] = useState([]);

  // Load employee list once for the dropdown
  useEffect(() => {
    api.get('/users/employees')
      .then((res) => {
        const d = res.data;
        const list = d?.users || d?.employees || d?.data || (Array.isArray(d) ? d : []);
        setEmployees(list);
      })
      .catch(() => {});
  }, []);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  // ── fetch whenever month/year changes ─────────────────────────────────────
  const [page,  setPage]  = useState(1);
  const [total, setTotal] = useState(0);

  // Debounce search by 300ms before firing the API call
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    const params = { month: filterMonth, year: filterYear, page, limit: PAGE_SIZE };
    if (debouncedSearch) params.search  = debouncedSearch;
    if (filterUserId)    params.user_id = filterUserId;
    api.get('/daily-updates', { params })
      .then((res) => {
        const d = res.data;
        const list = Array.isArray(d) ? d : d?.updates || d?.data || [];
        setUpdates(list);
        setTotal(d?.meta?.total ?? list.length);
      })
      .catch(() => setError('Failed to load updates. Please try again.'))
      .finally(() => setLoading(false));
  }, [filterMonth, filterYear, page, debouncedSearch, filterUserId]);

  // Reset to page 1 + clear date sub-filter when month/year/employee changes
  useEffect(() => { setPage(1); setDateFilter(''); }, [filterMonth, filterYear, filterUserId]);

  // Reset to page 1 when search changes
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    await api.delete(`/daily-updates/${id}`);
    setUpdates((prev) => prev.filter((u) => (u._id || u.id) !== id));
  };

  // ── group by employee (search is server-side; date sub-filter remains client-side) ─
  const grouped = useMemo(() => {
    const map = {};
    updates.forEach((u) => {
      const userId = u.user?._id || u.user?.id || u.userId || 'unknown';
      const name   = u.user?.name  || u.userName  || '';
      const email  = u.user?.username || u.user?.email_id || u.userEmail || '';
      if (!map[userId]) map[userId] = { userId, name, email, updates: [] };

      if (dateFilter) {
        const day = (u.date || u.createdAt || '').slice(0, 10);
        if (day !== dateFilter) return;
      }

      map[userId].updates.push(u);
    });
    return Object.values(map).filter((g) => g.updates.length > 0);
  }, [updates, dateFilter]);

  // ── summary stats ─────────────────────────────────────────────────────────
  const totalUpdates   = total;
  const todayStr       = toLocalDateString(today);
  const todayCount     = updates.filter((u) => (u.date || u.createdAt || '').slice(0, 10) === todayStr).length;
  const totalEmployees = useMemo(() => {
    const ids = new Set(updates.map((u) => u.user?._id || u.user?.id || u.userId));
    return ids.size;
  }, [updates]);

  const hasFilters = !!(search || dateFilter);

  return (
    <div className="p-7 space-y-6 bg-[#F8FAFC]">

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="label-meta mb-1">Team Activity</p>
          <h1 className="heading-md text-[#1F2937]">Daily Updates</h1>
          <p className="text-[#6B7280] text-sm mt-1">
            {loading ? 'Loading…' : `${totalUpdates} update${totalUpdates !== 1 ? 's' : ''} from ${totalEmployees} employee${totalEmployees !== 1 ? 's' : ''} — ${MONTH_NAMES[filterMonth - 1]} ${filterYear}`}
          </p>
        </div>
        <button
          onClick={load}
          className="btn flex items-center gap-2 text-[#6B7280]"
          style={{ padding: '0.6rem 1.1rem', fontSize: '13px' }}
        >
          <RiRefreshLine size={14} /> Refresh
        </button>
      </div>

      {/* ── Stats strip ───────────────────────────────────────────────────── */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Updates',       value: totalUpdates,   icon: RiFileList3Line, gradient: 'from-[#0984e3] to-[#00b0ff]', shadow: 'rgba(9,132,227,0.25)' },
          { label: 'Employees Reporting', value: totalEmployees, icon: RiUserLine,      gradient: 'from-[#7c3aed] to-[#d500f9]', shadow: 'rgba(213,0,249,0.25)' },
          { label: "Today's Updates",     value: todayCount,     icon: RiCalendarLine,  gradient: 'from-[#10b981] to-[#34d399]', shadow: 'rgba(16,185,129,0.25)' },
        ].map(({ label, value, icon: Icon, gradient, shadow }) => (
          <div key={label} className="bg-white border border-[#E5E7EB] rounded-2xl p-5 shadow-[0_4px_20px_rgba(15,23,42,0.06)]">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[#6B7280] text-sm">{label}</p>
              <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center`} style={{ boxShadow: `0 4px 12px ${shadow}` }}>
                <Icon size={17} className="text-white" />
              </div>
            </div>
            <p className="text-[#1F2937] text-3xl font-bold">{loading ? '—' : value}</p>
          </div>
        ))}
      </div>

      {/* ── Filter bar ────────────────────────────────────────────────────── */}
      <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] p-4">
        <div className="flex items-center gap-2 mb-3">
          <RiFilterLine size={14} className="text-[#0984e3]" />
          <span className="text-xs font-semibold text-[#374151] uppercase tracking-wide">Filters</span>
          {hasFilters && (
            <button
              onClick={() => { setSearch(''); setDateFilter(''); }}
              className="ml-auto text-xs text-[#0984e3] font-semibold hover:underline cursor-pointer flex items-center gap-1"
            >
              <RiCloseLine size={13} /> Clear client filters
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

          {/* Date (calendar dropdown — replaces Month + Year) */}
          <div>
            <label className="block text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1.5">Date</label>
            <div className="relative">
              <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" size={14} />
              <input
                type="date"
                value={dateFilter || `${filterYear}-${String(filterMonth).padStart(2, '0')}-01`}
                onChange={(e) => {
                  const val = e.target.value;
                  if (!val) { setDateFilter(''); return; }
                  const d = new Date(val + 'T00:00:00');
                  setFilterYear(d.getFullYear());
                  setFilterMonth(d.getMonth() + 1);
                  setDateFilter(val);
                }}
                className="input-field pl-8 pr-8 cursor-pointer text-sm w-full"
              />
              {dateFilter && (
                <button
                  type="button"
                  onClick={() => setDateFilter('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-red-500 cursor-pointer z-10"
                  title="Clear date — show full month"
                >
                  <RiCloseLine size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Employee */}
          <div>
            <label className="block text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1.5">Employee</label>
            <select
              value={filterUserId}
              onChange={(e) => setFilterUserId(e.target.value)}
              className="input-field cursor-pointer text-sm"
            >
              <option value="">All employees</option>
              {employees.map((u) => {
                const id = u.id || u._id;
                return (
                  <option key={id} value={id}>
                    {u.name || u.username || u.email_id}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1.5">Search</label>
            <div className="relative">
              <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" size={14} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, email or content…"
                className="input-field pl-8 text-sm"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#374151] cursor-pointer"
                >
                  <RiCloseLine size={14} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* active filter pills */}
        {(dateFilter || search || filterUserId) && (
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <span className="text-xs text-[#9CA3AF]">Active:</span>
            {filterUserId && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full text-[#10b981] bg-[rgba(16,185,129,0.08)] border border-[rgba(16,185,129,0.2)]">
                {(() => {
                  const u = employees.find((e) => (e.id || e._id) === filterUserId);
                  return u?.name || u?.username || u?.email_id || 'Employee';
                })()}
                <button onClick={() => setFilterUserId('')} className="ml-0.5 cursor-pointer hover:text-[#059669]"><RiCloseLine size={11} /></button>
              </span>
            )}
            {dateFilter && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full text-[#0984e3] bg-[rgba(9,132,227,0.08)] border border-[rgba(9,132,227,0.2)]">
                <RiCalendarLine size={11} />
                {new Date(dateFilter + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                <button onClick={() => setDateFilter('')} className="ml-0.5 cursor-pointer hover:text-[#0652DD]"><RiCloseLine size={11} /></button>
              </span>
            )}
            {search && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full text-[#7c3aed] bg-[rgba(124,58,237,0.08)] border border-[rgba(124,58,237,0.2)]">
                <RiSearchLine size={11} />
                "{search}"
                <button onClick={() => setSearch('')} className="ml-0.5 cursor-pointer hover:text-[#5b21b6]"><RiCloseLine size={11} /></button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Error ─────────────────────────────────────────────────────────── */}
      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center justify-between">
          {error}
          <button onClick={() => setError('')} className="cursor-pointer ml-2"><RiCloseLine size={14} /></button>
        </div>
      )}

      {/* ── Employee list ─────────────────────────────────────────────────── */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white border border-[#E5E7EB] rounded-2xl p-5 shadow-[0_4px_20px_rgba(15,23,42,0.06)]">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#F3F4F6] rounded-xl animate-pulse" />
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-[#F3F4F6] rounded w-1/4 animate-pulse" />
                  <div className="h-3 bg-[#F3F4F6] rounded w-1/3 animate-pulse" />
                </div>
                <div className="h-6 w-20 bg-[#F3F4F6] rounded-full animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : grouped.length === 0 ? (
        <div className="text-center py-16 bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)]">
          <div className="w-14 h-14 rounded-2xl bg-[#F3F4F6] flex items-center justify-center mx-auto mb-3">
            <RiFileList3Line size={28} className="text-[#D1D5DB]" />
          </div>
          <p className="text-[#374151] text-sm font-semibold">
            {hasFilters
              ? 'No updates match your filters.'
              : `No daily updates for ${MONTH_NAMES[filterMonth - 1]} ${filterYear}.`}
          </p>
          {hasFilters && (
            <button
              onClick={() => { setSearch(''); setDateFilter(''); }}
              className="mt-3 text-[#0984e3] text-sm font-medium hover:underline cursor-pointer"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map((group, idx) => (
            <EmployeeSection
              key={group.userId}
              name={group.name}
              email={group.email}
              updates={group.updates}
              defaultOpen={idx === 0}
              onDelete={handleDelete}
            />
          ))}
          {total > PAGE_SIZE && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] overflow-hidden">
              <Pagination page={page} total={total} onChange={setPage} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
